import {onUnmounted} from "vue";
import {storeToRefs} from "pinia";
import {aiAssistantApi} from "@/container";
import {parseStreamEvent, streamErrorStatus} from "@/core/http/streaming";
import useOrganizationId from "@/core/composables/useOrganizationId";
import {useAiAssistantStore} from "@/stores/aiAssistant.store";
import {MAX_HISTORY_TURNS} from "@/ai-assistant/constants";
import {requestContext} from "@/ai-assistant/lib/screenContext";
import {AssistantTurn, Proposition} from "@/ai-assistant/interfaces";

const UNAUTHORIZED = "Vous n'avez pas accès à l'assistant — reconnectez-vous ou contactez le support.";

/**
 * What the pharmacist can actually do about each failure.
 *
 * 401 and 403 are spelled out here rather than left to the shared response
 * interceptor: the stream runs on `fetch`, which the interceptor never sees.
 */
const ERRORS: Record<number, string> = {
    401: UNAUTHORIZED,
    403: UNAUTHORIZED,
    422: "La question n'a pas pu être traitée — reformulez-la.",
    429: 'Trop de questions en peu de temps — patientez une minute avant de réessayer.',
    503: 'Le service est momentanément indisponible — réessayez dans quelques instants.'
};

const GENERIC_ERROR = "L'assistant n'a pas pu répondre — réessayez.";

const NO_PROPOSITION_LOADED = "Les scénarios n'ont pas pu être chargés — la réponse ci-dessus reste valable.";

const SOME_PROPOSITIONS_LOST = "Un scénario n'a pas pu être chargé ; les autres sont affichés.";

/**
 * Drive the assistant conversation held in the store.
 *
 * Meant for the single drawer instance: it owns the in-flight request and
 * aborts it when it unmounts, so a second consumer would cut the first one's
 * answer short.
 */
const useAssistantChat = () => {
    const store = useAiAssistantStore();
    const {messages, isStreaming, error} = storeToRefs(store);
    const organizationId = useOrganizationId();

    /** The answer being written and the request writing it, or nothing. */
    let pending: { id: string; controller: AbortController } | null = null;

    /**
     * Whether a callback still belongs to the request in flight. A superseded
     * or stopped request must write nowhere.
     */
    const isCurrent = (controller: AbortController): boolean => pending?.controller === controller;

    /**
     * Close the answer: it stops being written and the panel accepts a new
     * question.
     */
    const settle = (answerId: string) => {
        store.finishMessage(answerId);
        store.isStreaming = false;
        pending = null;
    };

    /**
     * Consume one streamed chunk.
     */
    const handleChunk = (answerId: string, chunk: string) => {
        if (!chunk.trim()) {
            return;
        }

        const {event, data, ended} = parseStreamEvent(chunk);

        // Only the text event is consumed. An event type introduced later must
        // not be poured into the answer as though it were prose.
        if ((event === 'update' || event === '') && data) {
            store.appendToMessage(answerId, data);
        }

        if (ended) {
            settle(answerId);
        }
    };

    /**
     * Report a failure against the answer that was being written.
     */
    const fail = (answerId: string, cause: unknown) => {
        const answer = store.messages.find(message => message.id === answerId);

        // An answer that never started is noise, so it goes; one that got some
        // way through stays readable, and only stops being marked as written.
        if (answer?.content) {
            store.finishMessage(answerId);
        } else {
            store.removeMessage(answerId);
        }

        store.setError(ERRORS[streamErrorStatus(cause) ?? 0] ?? GENERIC_ERROR);
        store.isStreaming = false;
        pending = null;
    };

    /**
     * Ask the assistant a question.
     *
     * @param prompt
     */
    const ask = async (prompt: string) => {
        const asked = prompt.trim();

        if (!asked || store.isStreaming) {
            return;
        }

        if (!organizationId.value) {
            store.setError(GENERIC_ERROR);

            return;
        }

        store.setError(null);

        // The previous answer finishes writing itself before the next begins.
        store.revealAnswers();

        // Read before the new turns are appended: the question being asked is
        // the prompt, not a part of its own history.
        const history = store.historyTurns(MAX_HISTORY_TURNS);

        // Read at the moment of sending, and kept with the turn. The
        // pharmacist may change week while the answer is being written, and
        // this answer will have been computed for the week they were on — the
        // transcript has to be able to say so rather than let the advice pass
        // for advice about what is now on screen.
        const screen = store.screen;

        store.appendMessage('user', asked);

        const answerId = store.appendMessage('assistant', '', true, screen);
        const controller = new AbortController();

        pending = {id: answerId, controller};
        store.isStreaming = true;

        const turn = await aiAssistantApi().streamMessage(
            organizationId.value,
            store.assistantKey,
            {prompt: asked, history, context: requestContext(screen)},
            chunk => isCurrent(controller) && handleChunk(answerId, chunk),
            cause => isCurrent(controller) && fail(answerId, cause),
            controller
        );

        // A stream that ends without sending its terminator must not leave the
        // answer spinning for good.
        if (isCurrent(controller)) {
            settle(answerId);
        }

        // Judged on the abort and not on the request still being the pending
        // one: an ordinary end detaches it too, through the terminator, and
        // reading that as « no longer mine » would drop the scenarios of every
        // answer that arrived properly. What must be dropped is a turn the
        // pharmacist stopped or whose conversation they cleared — both of
        // which abort this controller.
        if (! controller.signal.aborted) {
            await loadPropositions(answerId, turn);
        }
    };

    /**
     * Fetch the scenarios a turn produced, once its text is written.
     *
     * They are read one by one rather than carried in the stream: a
     * proposition is large, it outlives the turn, and the identifiers are all
     * the answer needs to carry.
     *
     * A failure here leaves the answer standing. The text was produced from
     * the same figures, and losing the cards is not losing the answer — but
     * the pharmacist is told, because an answer that speaks of scenarios and
     * shows none reads as a proposition that failed.
     *
     * Settled rather than all-or-nothing: one scenario that fails to load must
     * not take down the two that arrived. And what is said about the shortfall
     * is attached to the answer, not to the panel, so it cannot surface over a
     * conversation the pharmacist has since left.
     */
    const loadPropositions = async (answerId: string, turn: AssistantTurn | null) => {
        const ids = turn?.propositions ?? [];

        if (!ids.length) {
            return;
        }

        store.expectPropositions(answerId);

        const results = await Promise.allSettled(
            ids.map(id => aiAssistantApi().proposition(id))
        );

        const loaded = results
            .filter((result): result is PromiseFulfilledResult<Proposition> => result.status === 'fulfilled')
            .map(result => result.value);

        const shortfall = loaded.length === results.length
            ? null
            : loaded.length ? SOME_PROPOSITIONS_LOST : NO_PROPOSITION_LOADED;

        store.attachPropositions(answerId, loaded, shortfall);
    };

    /**
     * Stop the answer being written, keeping what has arrived.
     */
    const stop = () => {
        const stopped = pending;

        // Including what had arrived and was still being written out: asking
        // for the answer to stop and watching it carry on would read as a
        // button that does nothing.
        store.revealAnswers();

        // Detached before the abort so the rejection it causes is not reported
        // as a failure: stopping is deliberate, not a breakdown.
        pending = null;

        if (stopped) {
            stopped.controller.abort();
            store.finishMessage(stopped.id);
        }

        store.isStreaming = false;
    };

    /**
     * Start over: nothing of the exchange is kept.
     */
    const clear = () => {
        stop();
        store.clear();
    };

    onUnmounted(stop);

    return {
        messages,
        isStreaming,
        error,
        ask,
        stop,
        clear
    };
};

export default useAssistantChat;
